import { fetchScreener, type ScreenerRow } from '../api/screener-client.js';

// Portfolio file import — turn an uploaded holdings list (xlsx / xls / csv /
// txt / pdf) into portfolio candidates and match them against the entity
// universe. Parsing is fully client-side (the file never leaves the browser);
// the heavy parsers (SheetJS, pdf.js) are dynamic imports so they cost nothing
// until a file is actually dropped.
//
// The flow is deliberately review-first: extract → match → let the user
// confirm. A holdings file is messy reality (headers, totals, footnotes), so
// the platform proposes and the user disposes — nothing is written until the
// explicit "Add to portfolio" click.

export interface ImportCandidate {
  /** The name as it appeared in the file. */
  raw: string;
  /** DB match, when one is convincing. */
  match: ScreenerRow | null;
  /** Entity already in the portfolio (matched entities only). */
  held: boolean;
  /** Whether the row is selected for adding (user-toggleable). */
  selected: boolean;
}

const MAX_CANDIDATES = 300;

// Legal-form suffixes across the 27 markets — used both to spot company names
// in unstructured text (PDF) and to strip noise before matching.
const LEGAL_SUFFIX =
  /\b(ltd\.?|limited|plc|llp|lp|gmbh|mbh|ag|ug|kg|kgaa|e\.?v\.?|sarl|sàrl|sa|sas|sasu|snc|sci|bv|b\.v\.|nv|n\.v\.|vof|aps|a\/s|a\.?s\.?|ab|oy|oyj|sp\.?\s?z\s?o\.?o\.?|s\.?r\.?o\.?|a\.?s\.?|kft|bt|zrt|nyrt|srl|s\.?r\.?l\.?|s\.?p\.?a\.?|s\.?n\.?c\.?|d\.?o\.?o\.?|sia|uab|oü|ou|se|ehf|hf)\s*$/i;

function stripLegalSuffix(name: string): string {
  let out = name.trim();
  // Strip up to two trailing legal tokens ("Care Group Holdings Ltd." etc.).
  for (let i = 0; i < 2; i++) {
    const next = out.replace(LEGAL_SUFFIX, '').replace(/[\s,.]+$/, '');
    if (next === out) break;
    out = next;
  }
  return out;
}

/** Normalise for comparison: casefold, strip punctuation + legal suffixes. */
export function normName(name: string): string {
  return stripLegalSuffix(name)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// A cell/line is name-like when it reads as words, not as a number, date,
// currency amount, URL or single letter.
function isNameLike(s: string): boolean {
  const t = s.trim();
  if (t.length < 3 || t.length > 120) return false;
  if (!/\p{L}{2}/u.test(t)) return false; // needs at least one real word
  if (/^[\d\s.,%€£$-]+$/.test(t)) return false; // numeric / amount
  if (/^\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}$/.test(t)) return false; // date
  if (/^(https?:\/\/|www\.)/i.test(t)) return false;
  return true;
}

const HEADER_WORDS =
  /^(company|companies|name|names|entity|entities|target|targets|holding|holdings|portfolio|asset|assets|business|operator|firma|societe|société|unternehmen)$/i;

// Pick the most name-like column of a row-major table and return its values.
function candidatesFromTable(rows: string[][]): string[] {
  if (rows.length === 0) return [];
  const width = Math.max(...rows.map((r) => r.length));
  if (width === 0) return [];

  // Header-driven pick first: a first-row cell that *is* a header word.
  const header = rows[0] ?? [];
  let col = header.findIndex((h) => HEADER_WORDS.test(h.trim()));
  let dataRows = rows;
  if (col >= 0) {
    dataRows = rows.slice(1);
  } else {
    // Otherwise score each column by its share of name-like cells.
    let bestScore = 0;
    col = 0;
    for (let c = 0; c < width; c++) {
      const vals = rows.map((r) => r[c] ?? '').filter((v) => v.trim() !== '');
      if (vals.length === 0) continue;
      const score = vals.filter(isNameLike).length / vals.length + Math.min(vals.length, 50) / 500;
      if (score > bestScore) {
        bestScore = score;
        col = c;
      }
    }
    // Drop a header row if it looks like one.
    if (header[col] && HEADER_WORDS.test(header[col]!.trim())) dataRows = rows.slice(1);
  }

  return dataRows.map((r) => (r[col] ?? '').trim()).filter(isNameLike);
}

// Minimal CSV parser (quoted fields, commas/semicolons). Good enough for
// holdings lists; no external dep.
function parseCsv(text: string): string[][] {
  const delim = (text.match(/;/g)?.length ?? 0) > (text.match(/,/g)?.length ?? 0) ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQ = false;
      } else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === delim) {
      row.push(cur);
      cur = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cur);
      cur = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else cur += ch;
  }
  row.push(cur);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  return rows;
}

async function extractFromSpreadsheet(file: File): Promise<string[]> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const names: string[] = [];
  for (const sheetName of wb.SheetNames) {
    const sheet = wb.Sheets[sheetName];
    if (!sheet) continue;
    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      raw: false,
      defval: '',
    }) as unknown as unknown[][];
    const asStrings = rows.map((r) => r.map((c) => (c == null ? '' : String(c))));
    names.push(...candidatesFromTable(asStrings));
    if (names.length >= MAX_CANDIDATES) break;
  }
  return names;
}

async function extractFromPdf(file: File): Promise<string[]> {
  const pdfjs = await import('pdfjs-dist');
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const lines: string[] = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    // Group text items into visual lines by their y-position.
    const byY = new Map<number, Array<{ x: number; str: string }>>();
    for (const item of content.items) {
      if (!('str' in item) || !item.str.trim()) continue;
      const y = Math.round(item.transform[5] as number);
      const x = item.transform[4] as number;
      let bucket = byY.get(y);
      // Tolerate 2px of baseline wobble.
      if (!bucket) {
        for (const [yy, b] of byY) {
          if (Math.abs(yy - y) <= 2) {
            bucket = b;
            break;
          }
        }
      }
      if (!bucket) {
        bucket = [];
        byY.set(y, bucket);
      }
      bucket.push({ x, str: item.str });
    }
    for (const [, items] of [...byY.entries()].sort((a, b) => b[0] - a[0])) {
      const line = items
        .sort((a, b) => a.x - b.x)
        .map((i) => i.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (line) lines.push(line);
    }
  }
  // Unstructured text: only lines that read as company names (legal suffix
  // somewhere in the line, or a short Title-Case phrase).
  const out: string[] = [];
  for (const line of lines) {
    if (!isNameLike(line)) continue;
    const m = line.match(
      new RegExp(`^(.{2,90}?${LEGAL_SUFFIX.source.replace(/\\s\*\$$/, '')})(?=\\s|$)`, 'i'),
    );
    if (m) out.push(m[1]!.trim());
    else if (LEGAL_SUFFIX.test(line) && line.length <= 90) out.push(line);
  }
  return out;
}

/** Extract candidate company names from an uploaded file. */
export async function extractCandidates(file: File): Promise<string[]> {
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  let names: string[];
  if (ext === 'xlsx' || ext === 'xls' || ext === 'ods') names = await extractFromSpreadsheet(file);
  else if (ext === 'csv') names = candidatesFromTable(parseCsv(await file.text()));
  else if (ext === 'pdf') names = await extractFromPdf(file);
  else if (ext === 'txt' || ext === 'tsv') {
    const text = await file.text();
    names =
      ext === 'tsv'
        ? candidatesFromTable(text.split(/\r?\n/).map((l) => l.split('\t')))
        : text.split(/\r?\n/).filter(isNameLike);
  } else {
    throw new Error(`Unsupported file type ".${ext}" — use xlsx, xls, csv, tsv, txt or pdf.`);
  }
  // Dedupe on the normalised name, keep first-seen raw form, cap the volume.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const n of names) {
    const key = normName(n);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(n.trim());
    if (out.length >= MAX_CANDIDATES) break;
  }
  return out;
}

/** Match candidate names against the entity universe (screener name search).
 *  Concurrency-limited; reports progress. */
export async function matchCandidates(
  names: string[],
  heldEntityIds: ReadonlySet<string>,
  onProgress: (done: number, total: number) => void,
): Promise<ImportCandidate[]> {
  const results: ImportCandidate[] = new Array(names.length);
  let done = 0;
  let next = 0;
  const CONCURRENCY = 4;

  async function matchOne(i: number): Promise<void> {
    const raw = names[i]!;
    let match: ScreenerRow | null = null;
    try {
      const q = stripLegalSuffix(raw).slice(0, 60);
      const res = await fetchScreener({ q, limit: 5 });
      const target = normName(raw);
      match =
        res.rows.find((r) => normName(r.companyName ?? '') === target) ??
        res.rows.find((r) => {
          const cand = normName(r.companyName ?? '');
          return cand.length > 4 && (cand.startsWith(target) || target.startsWith(cand));
        }) ??
        null;
    } catch {
      match = null; // search hiccup → offer as off-market row
    }
    const held = match != null && heldEntityIds.has(match.entityId);
    results[i] = { raw, match, held, selected: !held };
    done++;
    onProgress(done, names.length);
  }

  async function worker(): Promise<void> {
    while (next < names.length) {
      const i = next++;
      await matchOne(i);
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, names.length) }, worker));
  return results;
}
